import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, Send } from 'lucide-react';
import { teamBuildingActivities } from '../data/corporateTeamBuilding';
import { sendCorporateTeamBuildingLeadToMake } from '../utils/makeWebhook';
import { getUtmParams } from '../utils/utmTracking';
const GROUP_SIZES = ['עד 20 משתתפים', '20-50 משתתפים', '50-100 משתתפים', 'מעל 100 משתתפים'];
interface FormState {
  fullName: string;
  company: string;
  phone: string;
  email: string;
  groupSize: string;
  preferredDate: string;
  noDateYet: boolean;
  activities: string[];
  notes: string;
}
const INITIAL_STATE: FormState = {
  fullName: '',
  company: '',
  phone: '',
  email: '',
  groupSize: '',
  preferredDate: '',
  noDateYet: false,
  activities: [],
  notes: ''
};
export function CorporateEventBuilderForm() {
  const [formData, setFormData] = useState<FormState>(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const toggleNoDateYet = () => {
    setFormData(prev => ({
      ...prev,
      noDateYet: !prev.noDateYet,
      preferredDate: !prev.noDateYet ? '' : prev.preferredDate
    }));
  };
  const toggleActivity = (title: string) => {
    setFormData(prev => ({
      ...prev,
      activities: prev.activities.includes(title) ? prev.activities.filter(activity => activity !== title) : [...prev.activities, title]
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.phone.trim() || !formData.email.trim()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    const utmParams = getUtmParams();
    const wasSent = await sendCorporateTeamBuildingLeadToMake({
      type: 'ליד ימי גיבוש לחברות וארגונים',
      source: 'דף נחיתה ימי גיבוש לחברות וארגונים',
      fullName: formData.fullName.trim(),
      company: formData.company.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      groupSize: formData.groupSize,
      preferredDate: formData.preferredDate,
      noDateYet: formData.noDateYet,
      activities: formData.activities,
      notes: formData.notes.trim(),
      submittedAt: new Date().toISOString(),
      pageUrl: window.location.href,
      referrer: document.referrer,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_content: utmParams.utm_content,
      utm_term: utmParams.utm_term,
      utm_id: utmParams.utm_id,
      fbclid: utmParams.fbclid
    });
    setIsSubmitting(false);
    if (!wasSent) {
      setSubmitError('לא הצלחנו לשלוח את הפרטים כרגע. בדקו את החיבור ונסו שוב בעוד רגע.');
      return;
    }
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', 'Lead_Corporate_LP', {
        content_name: 'Corporate Team Building',
        content_category: 'Groups and Organizations',
        currency: 'ILS'
      });
    }
    setIsSubmitted(true);
  };
  const inputClass = 'w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-colors';
  if (isSubmitted) {
    return <div className="text-center py-10" role="status" data-id="element-136" data-mp="1v4vizyyvbp7a">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-137" data-mp="200ufyay9ifjb">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" data-id="element-138" data-mp="jmujj9yml80t" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-139" data-mp="omyz8xrplypz">
          הפרטים התקבלו!
        </h3>
        <p className="text-gray-600 max-w-md mx-auto" data-id="element-140" data-mp="14u3gnzmrn909">
          נחזור אליכם תוך יום עסקים אחד עם האפשרויות המתאימות ולוח זמנים ראשוני
          ליום הגיבוש.
        </p>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-141" data-mp="v38xj44tgpj1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-142" data-mp="v1pbewwhbk9c">
        <div data-id="element-143" data-mp="q6ifwcdd5lvi">
          <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-144" data-mp="q6s6o268fx29">
            שם מלא <span className="text-rose-500" data-id="element-145" data-mp="1itw7rw27fr8r">*</span>
          </label>
          <input id="fullName" type="text" name="fullName" value={formData.fullName} onChange={handleChange} required disabled={isSubmitting} autoComplete="name" placeholder="איך קוראים לך?" className={inputClass} data-id="element-146" data-mp="1irjsjqntzuze" />
        </div>
        <div data-id="element-147" data-mp="3tkmmjew7kpq2">
          <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-148" data-mp="3tk4frobx6t3w">
            שם החברה או הארגון
          </label>
          <input id="company" type="text" name="company" value={formData.company} onChange={handleChange} disabled={isSubmitting} autoComplete="organization" placeholder="שם החברה" className={inputClass} data-id="element-149" data-mp="2e3rm37pf4u3l" />
        </div>
        <div data-id="element-150" data-mp="2stp2h1h3wfi8">
          <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-151" data-mp="2wt7ok0knywmn">
            טלפון <span className="text-rose-500" data-id="element-152" data-mp="31s10hokayfkp">*</span>
          </label>
          <input id="phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} required disabled={isSubmitting} autoComplete="tel" dir="ltr" placeholder="050-1234567" className={`${inputClass} text-right`} data-id="element-153" data-mp="36rdsenq16cu5" />
        </div>
        <div data-id="element-154" data-mp="14ehup8pcis2s">
          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-155" data-mp="10b35brfiozco">
            אימייל <span className="text-rose-500" data-id="element-156" data-mp="1ebvwq3aivebk">*</span>
          </label>
          <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting} autoComplete="email" dir="ltr" placeholder="name@company.co.il" className={`${inputClass} text-right`} data-id="element-157" data-mp="19c17e46eawsm" />
        </div>
        <div data-id="element-158" data-mp="1s2izc6jelocx">
          <label htmlFor="groupSize" className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-159" data-mp="1s1r4wz1jlwo8">
            כמה אתם?
          </label>
          <select id="groupSize" name="groupSize" value={formData.groupSize} onChange={handleChange} disabled={isSubmitting} className={inputClass} data-id="element-160" data-mp="220f8isss698e">
            <option value="" data-id="element-161" data-mp="2po3qbby4q1tg">בחרו גודל קבוצה</option>
            {GROUP_SIZES.map(size => <option key={size} value={size} data-id="element-162" data-mp="2bou9qb60h5oy">
                {size}
              </option>)}
          </select>
        </div>
        <div data-id="element-163" data-mp="2glaxh49ati1i">
          <label htmlFor="preferredDate" className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-164" data-mp="2gp8g7orys7kv">
            תאריך מבוקש{' '}
            <span className="font-normal text-gray-400" data-id="element-165" data-mp="26pretjjqwfup">(אופציונלי)</span>
          </label>
          <input id="preferredDate" type="date" lang="he-IL" name="preferredDate" value={formData.preferredDate} onChange={handleChange} disabled={formData.noDateYet || isSubmitting} className={`${inputClass} text-right disabled:opacity-50 disabled:bg-gray-100`} data-id="element-166" data-mp="1symeef8jzjjb" />
          <button type="button" onClick={toggleNoDateYet} disabled={isSubmitting} aria-pressed={formData.noDateYet} className={`mt-2 inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-3 py-1.5 border transition-colors ${formData.noDateYet ? 'bg-[#0A5F8F] text-white border-[#0A5F8F]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A5F8F]'}`} data-id="element-167" data-mp="23gzvjvansh7o">
            עדיין אין לנו תאריך
          </button>
        </div>
      </div>

      <fieldset disabled={isSubmitting} data-id="element-168" data-mp="1fpuh9kkd9wzh">
        <legend className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-169" data-mp="1fosu4hq2tk1e">
          מה מעניין אתכם ביום הזה?{' '}
          <span className="font-normal text-gray-400" data-id="element-170" data-mp="16q7eovfwil4u">(אופציונלי)</span>
        </legend>
        <div className="flex flex-wrap gap-2" data-id="element-171" data-mp="16oe4x79zrksm">
          {teamBuildingActivities.map(activity => {
          const active = formData.activities.includes(activity.title);
          return <button key={activity.id} type="button" onClick={() => toggleActivity(activity.title)} aria-pressed={active} className={`py-2 px-4 rounded-full text-sm font-semibold border transition-colors ${active ? 'bg-[#0A5F8F] text-white border-[#0A5F8F]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#0A5F8F]'}`} data-id="element-172" data-mp="wuds0j8gxb9c">
                {activity.title}
              </button>;
        })}
        </div>
      </fieldset>

      <div data-id="element-173" data-mp="8w6fvj69q6rr">
        <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-174" data-mp="8xra17oo7np3">
          ספרו לנו על הקבוצה ועל היום שאתם מדמיינים{' '}
          <span className="font-normal text-gray-400" data-id="element-175" data-mp="in11qmflr4co">(אופציונלי)</span>
        </label>
        <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} disabled={isSubmitting} rows={3} placeholder="אופי הקבוצה, מטרות היום, אוכל, שעות, רעיונות מיוחדים..." className={`${inputClass} resize-none`} data-id="element-176" data-mp="3hcj64rg3j0br" />
      </div>

      {submitError && <div role="alert" className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4" data-id="element-177" data-mp="ms4unerbqxrk">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-178" data-mp="vv61jho0kswa" />
          <p className="text-sm" data-id="element-179" data-mp="vsjsfdlh65by">{submitError}</p>
        </div>}

      <button type="submit" disabled={isSubmitting} className="w-full bg-[#0A5F8F] hover:bg-[#0D7AB8] disabled:opacity-70 disabled:cursor-wait text-white font-bold text-lg py-4 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-180" data-mp="3vb2kt6hw3x6o">
        {isSubmitting ? <>
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-181" data-mp="cxmp042cks54" />
            שולחים את הפרטים...
          </> : <>
            <Send className="w-5 h-5" data-id="element-182" data-mp="3gj2685ewbcc8" />
            בואו נתכנן את יום הגיבוש
          </>}
      </button>
      <p className="text-center text-sm text-gray-500" data-id="element-183" data-mp="38z9onn6jk3mc">
        ללא התחייבות — לאחר השליחה נחזור אליכם עם האפשרויות המתאימות.
      </p>
    </form>;
}